import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const IntentSummaryCard = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📊 Intent Tracking
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Nessun dato disponibile
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getIntentIcon = (intent) => {
    const icons = {
      'line_activation': '📱',
      'business_affiliation': '🏢'
    };
    return icons[intent] || '📋';
  };

  const getIntentLabel = (intent) => {
    const labels = {
      'line_activation': 'Attivazione Linea',
      'business_affiliation': 'Affiliazione Business'
    };
    return labels[intent] || intent;
  };

  const totalIntents = data.reduce((sum, intent) => sum + intent.total_count, 0);
  const totalCompleted = data.reduce((sum, intent) => sum + intent.completed_count, 0);
  const totalPending = data.reduce((sum, intent) => sum + intent.pending_count, 0);
  const totalCancelled = data.reduce((sum, intent) => sum + intent.cancelled_count, 0);
  const overallCompletionRate = totalIntents > 0 ? ((totalCompleted / totalIntents) * 100).toFixed(1) : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            📊 Intent Tracking
          </Typography>
          <Chip
            label={`${totalIntents} richieste`}
            color="primary"
            size="small"
          />
        </Box>

        {/* Overall Stats */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={4}>
            <Box textAlign="center" p={1} bgcolor="success.lighter" borderRadius={2}>
              <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
              <Typography variant="h6" color="success.main" fontWeight="bold">
                {totalCompleted}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Completati
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center" p={1} bgcolor="warning.lighter" borderRadius={2}>
              <PendingIcon color="warning" sx={{ fontSize: 28 }} />
              <Typography variant="h6" color="warning.main" fontWeight="bold">
                {totalPending}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                In Attesa
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center" p={1} bgcolor="error.lighter" borderRadius={2}>
              <CancelIcon color="error" sx={{ fontSize: 28 }} />
              <Typography variant="h6" color="error.main" fontWeight="bold">
                {totalCancelled}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Annullati
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Overall Completion Rate */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" fontWeight="bold" color="textSecondary">
              Tasso di Completamento Totale
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {overallCompletionRate >= 70 ? (
                <TrendingUpIcon color="success" fontSize="small" />
              ) : (
                <TrendingDownIcon color="error" fontSize="small" />
              )}
              <Typography variant="h6" fontWeight="bold">
                {overallCompletionRate}%
              </Typography>
            </Stack>
          </Box>
          <LinearProgress
            variant="determinate"
            value={parseFloat(overallCompletionRate)}
            color={
              overallCompletionRate >= 70 ? 'success' : 
              overallCompletionRate >= 40 ? 'warning' : 'error'
            }
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>

        {/* Intent Breakdown */}
        <Typography variant="subtitle2" color="textSecondary" mb={1}>
          Breakdown per Tipo:
        </Typography>
        <Stack spacing={2}>
          {data.map((intent) => (
            <Box key={intent.intent}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    {getIntentIcon(intent.intent)}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {getIntentLabel(intent.intent)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={`${intent.total_count}`}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="caption" fontWeight="bold" color="primary">
                    {intent.completion_rate}%
                  </Typography>
                </Stack>
              </Box>
              <LinearProgress
                variant="determinate"
                value={intent.completion_rate}
                color={
                  intent.completion_rate >= 70 ? 'success' : 
                  intent.completion_rate >= 40 ? 'warning' : 'error'
                }
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Box display="flex" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" color="success.main">
                  ✓ {intent.completed_count}
                </Typography>
                <Typography variant="caption" color="warning.main">
                  ⏳ {intent.pending_count}
                </Typography>
                <Typography variant="caption" color="error.main">
                  ✗ {intent.cancelled_count}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default IntentSummaryCard;
